import { Component, OnInit, signal, computed, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { InternalReturn } from '../internal-return.component';
import { formatDateWithLocale } from '@shared/utils/date-formatter.util';
import { AfterSaleHeaderControllerService } from '@app/api/order-service/api/afterSaleHeaderController.service';
import { OrderDetailSearchDTO } from '@app/api/order-service/model/orderDetailSearchDTO';
import { RefundDetailsVO } from '@app/api/order-service/model/refundDetailsVO';
import { STATUS_MAP } from '../internal-return.config';

@Component({
  selector: 'app-internal-return-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    DataTableComponent,
    AppButtonComponent,
    ProgressSpinnerModule,
    TagModule,
  ],
  providers: [MessageService],
  templateUrl: './internal-return-view.component.html',
  styleUrl: './internal-return-view.component.css',
})
export class InternalReturnViewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private afterSaleHeaderService = inject(AfterSaleHeaderControllerService);
  private destroy$ = new Subject<void>();

  // Data signals
  loading = signal(false);
  formData = signal<InternalReturn | null>(null);
  productDetails = signal<any[]>([]);
  productTableColumns = signal<TableColumn[]>([]);

  // Computed title
  pageTitle = computed(() => 'after.internal.return.view.title');

  ngOnInit(): void {
    this.loadViewData();
    this.initializeProductTableColumns();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Navigation handlers
  onBackClick(): void {
    this.router.navigate(['/after/internal/return']);
  }

  private loadViewData(): void {
    this.loading.set(true);
    
    const idOrCode = this.route.snapshot.paramMap.get('id');
    
    if (!idOrCode) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('after.internal.return.view.messages.failed_to_load'),
      });
      this.router.navigate(['/after/internal/return']);
      return;
    }

    // Determine if idOrCode is a number (orderId) or string (orderCode)
    const orderId = Number(idOrCode);
    const isNumeric = !isNaN(orderId) && isFinite(orderId);

    const payload: OrderDetailSearchDTO = isNumeric
      ? { orderId: orderId }
      : { orderCode: idOrCode };

    this.afterSaleHeaderService
      .queryOrderBaseDetailByDTO2(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: RefundDetailsVO) => {
          // Handle blob response
          if (response instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const data = JSON.parse(reader.result as string);
                this.processResponse(data);
              } catch (error) {
                console.error('Error parsing blob response:', error);
                this.handleError(error);
              }
            };
            reader.onerror = () => this.handleError(reader.error);
            reader.readAsText(response);
          } else {
            this.processResponse(response);
          }
        },
        error: (error) => {
          this.handleError(error);
        },
      });
  }

  private processResponse(response: RefundDetailsVO): void {
    const baseInfo = response.refundBaseInfoVO;
    const buyerInfo = response.refundBuyerInfoVO;
    const logisticsInfo = response.refundLogisticsInfoVO;
    const items = response.items || [];

    if (!baseInfo) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('after.internal.return.view.messages.failed_to_load'),
      });
      this.loading.set(false);
      return;
    }

    // Map status from number to constant
    const statusNumber = String(baseInfo.status || '');
    const mappedStatus = STATUS_MAP[statusNumber] || statusNumber;
    
    // Calculate total refund amount from items
    const totalRefundAmount = items.reduce((sum, item) => {
      const quantity = item.quantity || 0;
      const unitPrice = item.unitPrice || 0;
      return sum + (quantity * unitPrice);
    }, 0);
    
    // Map to InternalReturn
    const mappedData: InternalReturn = {
      internalReturnNo: baseInfo.innerRefundNo || '',
      internalOrderNo: baseInfo.innerOrderNo || '',
      thirdReturnNo: baseInfo.outerRefundNo || '',
      returnOrderType: baseInfo.orderRefundType || '',
      thirdOrderNo: baseInfo.outerOrderNo || '',
      returnType: baseInfo.refundType === '1' ? 'Refund Only' : baseInfo.refundType === '2' ? 'Return & Refund' : '',
      status: mappedStatus,
      returnOrderSource: baseInfo.actionType || '',
      returnRefundReason: baseInfo.reason || '',
      buyerNotes: baseInfo.buyerRemarks || '',
      csNotes: baseInfo.serviceRemarks || '',
      returnQty: items.reduce((sum, item) => sum + (item.quantity || 0), 0),
      buyerRefundTotal: totalRefundAmount,
      settlementMethod: baseInfo.payType || '',
      receivingWarehouse: logisticsInfo?.receivingWarehouseName || '',
      returnLogisticsInfo: logisticsInfo?.logisticsList?.join(', ') || '',
      buyerNickname: buyerInfo?.buyerNick || '',
      returnerName: buyerInfo?.receiverName || '',
      returnerPhone: buyerInfo?.mobile || '',
      returnerAddress: buyerInfo?.address || '',
      salesStore: baseInfo.storeName || '',
      salesChannel: baseInfo.channelTypeMeaning || baseInfo.channel || '',
      thirdReturnTime: baseInfo.outReturnTime || '',
      creationDate: baseInfo.creationDate || '',
      returnCompletedTime: baseInfo.completeDate || '',
    };

    this.formData.set(mappedData);
    this.loadProductDetails(items);
    this.loading.set(false);
  }

  private loadProductDetails(items: any[]): void {
    const mappedProducts = items.map((item) => ({
      skuCode: item.skuCode || '',
      productName: item.skuName || item.spuName || '',
      returnQty: item.quantity || 0,
      unitPrice: item.unitPrice || 0,
      totalPrice: (item.quantity || 0) * (item.unitPrice || 0),
    }));

    this.productDetails.set(mappedProducts);
  }

  private handleError(error: any): void {
    console.error('Error loading return order details:', error);
    this.loading.set(false);
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: error?.error?.message || this.translateService.instant('after.internal.return.view.messages.failed_to_load'),
    });
  }

  private initializeProductTableColumns(): void {
    this.productTableColumns.set([
      {
        label: 'after.internal.return.view.product_table.sku_code',
        prop: 'skuCode',
        width: 150,
      },
      {
        label: 'after.internal.return.view.product_table.product_name',
        prop: 'productName',
        width: 200,
      },
      {
        label: 'after.internal.return.view.product_table.return_qty',
        prop: 'returnQty',
        width: 120,
      },
      {
        label: 'after.internal.return.view.product_table.unit_price',
        prop: 'unitPrice',
        width: 120,
        type: 'currency',
      },
      {
        label: 'after.internal.return.view.product_table.total_price',
        prop: 'totalPrice',
        width: 120,
        type: 'currency',
      },
    ]);
  }

  getFieldValue(field: keyof InternalReturn): string {
    return this.formData()?.[field]?.toString() || '-';
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    return formatDateWithLocale(dateString);
  }

  getStatusLabel(status?: string): string {
    if (!status) return '-';
    
    // Map status constant to translation key
    const statusKeyMap: Record<string, string> = {
      'WAIT_AUDIT': 'after.internal.return.status.wait_audit',
      'WAIT_REFUND': 'after.internal.return.status.wait_refund',
      'COMPLETE': 'after.internal.return.status.completed',
      'REFUSE': 'after.internal.return.status.rejected',
      'REFUND_FAILED': 'after.internal.return.status.refund_failed',
    };
    
    const translationKey = statusKeyMap[status] || status;
    return this.translateService.instant(translationKey);
  }

  getStatusTagClass(status?: string): string {
    if (!status) return '';
    
    // Map status constant to tag class
    const statusClassMap: Record<string, string> = {
      'WAIT_AUDIT': 'p-tag-warning',
      'WAIT_REFUND': 'p-tag-info',
      'COMPLETE': 'p-tag-success',
      'REFUSE': 'p-tag-danger',
      'REFUND_FAILED': 'p-tag-secondary',
    };
    
    return statusClassMap[status] || 'p-tag-info';
  }

  formatCurrency(value: number | string | undefined): string {
    if (value === undefined || value === null) return '-';
    if (value === 0) return '0 ₫';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '-';
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(numValue);
  }

  formatSettlementMethod(method?: string): string {
    if (!method) return '-';
    // Capitalize first letter and lowercase the rest
    return method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
  }
}
